import { get } from 'lodash'

import { Colors, Services, getServicePathToAPI, getString, joinURLPath } from '@sigma-frontend-libs/commons'
import { Macro } from '@sigma-legacy-libs/cache'
import { isUUID } from '@sigmasms/commons'

import { globalErrorHandler, macroCacheOptions, permissionPresets, projectName, request } from '@/utils'

import render from './render'

const Cache = new Macro(macroCacheOptions)
const pathToAPI = getServicePathToAPI(Services.users)

const cachedGet = Cache.wrapWithCache(async (key, id) => {
  return await request.get(joinURLPath(pathToAPI, id))
})

export default {
  name: 'Owner',

  props: {
    value: {
      type: [ Object, String ],
      default: () => {
        return {}
      }
    },

    label: {
      type: [ String, Number, Boolean ],
      default: undefined
    },

    copy: Boolean,
    readonly: Boolean,

    pathToData: {
      type: String,
      default: 'Owner'
    },

    accept: {
      type: Array,
      default: () => {
        return permissionPresets.resellerUp
      }
    },

    minWidth: {
      type: [ String, Number ],
      default: undefined
    },
    maxWidth: {
      type: [ String, Number ],
      default: undefined
    },
    width: {
      type: [ String, Number ],
      default: undefined
    },

    compact: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      data: undefined,
      ownerData: undefined,
      getDataRequestNumber: 0,

      loading: false
    }
  },

  computed: {
    Id() {
      return get(this.data, this.pathToData + 'Id')
    },
    id() {
      return get(this.data, this.pathToData.toLowerCase() + 'Id')
    },
    $_id() {
      if (typeof this.value === 'string') {
        return this.value
      }

      return this.Id || this.id
    },

    Owner() {
      return get(this.data, this.pathToData)
    },
    owner() {
      return get(this.data, this.pathToData.toLowerCase())
    },
    ownerAsObject() {
      const data = [ this.Owner, this.owner, this.ownerData ].filter(Boolean)

      return data.find(item => {
        return typeof item === 'object'
      })
    },
    shouldGetOwnerData() {
      if (!this.canGetUsers) {
        return false
      }

      if (!isUUID(this.$_id)) {
        return false
      }

      if (this.ownerAsObject) {
        return false
      }

      return true
    },
    $_owner() {
      if (this.ownerAsObject) {
        return this.ownerAsObject
      }

      if (this.shouldGetOwnerData) {
        return undefined
      }

      if (this.Owner !== undefined) {
        return this.Owner
      }

      if (this.owner !== undefined) {
        return this.owner
      }

      return this.$_id
    },

    hasOwner() {
      return Boolean(this.$_owner)
    },

    canGetUsers() {
      return this.checkPermissions(`advanced.${Services.users}.get`, this.accept)
    },

    verified() {
      return get(this.$_owner, '$verified', false)
    },

    $_label() {
      return getString(this.label)
    },

    $_props() {
      let color = Colors.grey
      if (this.verified) {
        color = Colors.info
      }

      const result = {
        value: this.$_owner,
        pathToTitle: 'username',
        label: this.$_label || this.getTranslate('commons.labels.owner'),
        color,
        loading: this.loading,
        readonly: this.readonly,
        miWidth: this.minWidth,
        maxWidth: this.maxWidth,
        width: this.width,
        copy: this.copy
      }

      if (!this.compact && !this.shouldGetOwnerData) {
        result.iconSVG = 'user'
        result.iconTooltip = undefined
        if (this.verified) {
          result.iconSVG = 'user-verified'
          result.iconTooltip = this.getTranslate('commons.has.contract')
        }
      }

      return result
    }
  },

  watch: {
    value: {
      handler() {
        this.getData()
      },
      deep: true
    }
  },

  mounted() {
    this.getData()
  },

  methods: {
    async getData() {
      this.getDataRequestNumber += 1
      const currentRequestNumber = this.getDataRequestNumber

      try {
        this.ownerData = undefined

        if (this.value) {
          if (typeof this.value === 'object') {
            this.data = this.value
          } else {
            this.data = undefined
          }

          if (this.shouldGetOwnerData) {
            const requestedOwnerId = this.$_id

            this.loading = true
            const key = [ projectName, Services.users, 'get', requestedOwnerId ].filter(Boolean).join(':')
            const data = await cachedGet(key, requestedOwnerId)

            if (data && currentRequestNumber === this.getDataRequestNumber) {
              this.ownerData = data
            }
          }
        } else {
          this.data = undefined
        }
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        if (currentRequestNumber === this.getDataRequestNumber) {
          this.loading = false
        }
      }
    }
  },

  render
}
